/* ==========================================================================
   Siraat — make the quick-buy modal fail instead of hanging
   --------------------------------------------------------------------------
   The theme's QuickBuyModal.show() builds its readiness promise with an async
   executor:

       const promise = new Promise(async (resolve, reject) => {
         const responseContent = await (await cachedFetch(productUrl)).text();
         const tempDoc = new DOMParser().parseFromString(responseContent, "text/html");
         const quickBuyContent = tempDoc.getElementById("quick-buy-content");
         this.replaceChildren(quickBuyContent.content.cloneNode(true).firstElementChild);
         resolve();
       });

   A throw inside an async executor is swallowed: reject is never reached, so
   the promise never settles. Every failure in there, a non-200, a page without
   the template, a parse that comes back empty, turns into a promise that hangs
   forever. The modal never opens, aria-busy is never cleared, and the
   theme:loading:end that hides the cart drawer's spinner is never dispatched.
   The customer is left watching a spinner with no way out but a reload, which
   is what the Stainless Steel Lid was doing from the cart drawer upsell.

   This replaces show() with the same sequence written so it always settles:

     - fetch, parse and look for the template exactly as the theme does, so a
       working product page behaves identically
     - every exit clears aria-busy and dispatches the matching loading:end
     - when the template genuinely is not there, send the customer to the
       product page rather than leaving them on a dead button. They can still
       pick a variant and buy; it is one navigation instead of a lost sale.

   Patching the prototype rather than forking the class: the element is already
   defined by the time this runs, and everything else about it, the animation,
   the focus trap, the overlay, is the theme's and should stay the theme's.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__siraatQuickBuyFix) return;
  window.__siraatQuickBuyFix = true;

  if (!window.customElements || !window.customElements.whenDefined) return;

  window.customElements.whenDefined('quick-buy-modal').then(function () {
    var ctor = window.customElements.get('quick-buy-modal');
    if (!ctor || !ctor.prototype) return;

    var proto = ctor.prototype;
    /* Modal.prototype.show, i.e. what the original reached through super */
    var parentProto = Object.getPrototypeOf(proto);
    if (!parentProto || typeof parentProto.show !== 'function') return;
    var parentShow = parentProto.show;

    proto.show = function () {
      var self = this;
      /* Modal.show sets the open attribute, and the theme's attributeChangedCallback answers that by calling
         this.show() again, synchronously. Without this guard that nested call fetched the page a second time and
         opened the dialog twice, taking two scroll locks that a single close could not release. */
      if (self.__siraatInnerShow) return Promise.resolve();
      var url = self.getAttribute('product-url');
      var controls = [];
      try { controls = self.controls || []; } catch (e) {}

      /* The theme shows the global loading bar only when no control can show a
         busy state of its own. The cart drawer's ADD button has no
         <button-content>, so this is the branch the upsell takes. */
      var showLoadingBar = controls.every(function (c) {
        return c.querySelector('button-content') === null;
      });

      var busy = [self].concat(controls);
      busy.forEach(function (c) { c.setAttribute('aria-busy', 'true'); });
      if (showLoadingBar) {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:start', { bubbles: true })
        );
      }

      var settled = false;
      function release() {
        if (settled) return;
        settled = true;
        busy.forEach(function (c) { c.setAttribute('aria-busy', 'false'); });
        if (showLoadingBar) {
          document.documentElement.dispatchEvent(
            new CustomEvent('theme:loading:end', { bubbles: true })
          );
        }
      }

      if (!url) {
        release();
        return Promise.resolve();
      }

      /* Fetch first, then open with an already-settled condition. The theme
         also waits for the fetch before the modal appears, so this is the same
         wait, just one that cannot become infinite. */
      return fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('quick buy: HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var tpl = doc.getElementById('quick-buy-content');
          var node = tpl && tpl.content ? tpl.content.firstElementChild : null;
          if (!node) throw new Error('quick buy: no #quick-buy-content on ' + url);

          self.replaceChildren(node.cloneNode(true));
          release();
          self.__siraatInnerShow = true;
          try {
            return parentShow.call(self, { conditionToFulfill: Promise.resolve() });
          } finally {
            self.__siraatInnerShow = false;
          }
        })
        .catch(function (error) {
          release();
          /* Left in on purpose. If this ever fires the console names the URL
             that has no quick-buy template, which is the one thing that cannot
             be worked out by reading the theme. */
          if (window.console && console.warn) {
            console.warn('[siraat] quick buy fell back to the product page.', error);
          }
          window.location.href = url;
        });
    };
  }).catch(function () {});
})();
